import React, { useEffect, useMemo, useState } from 'react';
import { X, Lock, KeyRound, ShieldCheck, Plus, Edit3, Trash2, Star, Save, Inbox, LibraryBig, ChevronLeft, ShieldAlert, Check, Upload, BookOpen } from 'lucide-react';
import api from '../api/client';
import { IMAGES, categories as MED_CATS, affirmationCategories as AFF_CATS } from '../data/mock';
import BlogManager from './BlogManager';

const extractYouTubeId = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  try {
    const short = s.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (short) return short[1];
    const path = s.match(/youtube\.com\/(?:embed|shorts|v)\/([A-Za-z0-9_-]{11})/);
    if (path) return path[1];
    const url = new URL(s.startsWith('http') ? s : `https://${s}`);
    const v = url.searchParams.get('v');
    if (v && /^[A-Za-z0-9_-]{11}$/.test(v)) return v;
  } catch (_e) { /* ignore */ }
  return '';
};

const FeedbackAdminRow = ({ f, onToggleVisibility, onDelete, onSaveReply }) => {
  const [replyOpen, setReplyOpen] = useState(!!f.reply);
  const [replyText, setReplyText] = useState(f.reply || '');
  const [busy, setBusy] = useState(false);

  const quickThanks = 'Thank you so much for your beautiful words — they mean the world to me. Sending peace and calm your way. 🙏';

  const save = async () => {
    setBusy(true);
    try { await onSaveReply(replyText.trim()); } finally { setBusy(false); }
  };
  const clearReply = async () => {
    setBusy(true);
    try { await onSaveReply(''); setReplyText(''); setReplyOpen(false); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-emerald-100 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium text-emerald-950">{f.name}</div>
        <span className="text-[10px] uppercase tracking-widest rounded-full bg-emerald-50 border border-emerald-100 px-2 py-1 text-emerald-800">{f.type}</span>
      </div>
      <p className="mt-2 text-sm text-emerald-900/90 whitespace-pre-wrap">{f.message}</p>
      <div className="mt-1 text-[11px] text-emerald-700 flex flex-wrap gap-2 justify-between">
        <span>Rating: {f.rating}/5 · {f.date}</span>
        <span>{f.visible ? 'Public' : 'Hidden'}</span>
      </div>

      {f.reply && !replyOpen && (
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-900">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-1">Your Reply · {f.replyDate}</div>
          {f.reply}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setReplyOpen((v) => !v)} className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">
          {replyOpen ? 'Close reply' : f.reply ? 'Edit reply' : 'Reply'}
        </button>
        <button type="button" onClick={() => { setReplyText(quickThanks); setReplyOpen(true); }} className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">Quick Thank-you</button>
        <button type="button" onClick={onToggleVisibility} className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">
          {f.visible ? 'Hide from wall' : 'Show on wall'}
        </button>
        <button type="button" onClick={onDelete} className="text-xs px-3 py-1.5 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50">Delete</button>
      </div>

      {replyOpen && (
        <div className="mt-3 space-y-2">
          <textarea rows={3} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Write a warm reply — this appears publicly under the feedback." className="w-full px-3 py-2 rounded-lg border border-emerald-200 text-sm" />
          <div className="flex items-center gap-2">
            <button type="button" disabled={busy} onClick={save} className="text-xs px-3 py-1.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">{busy ? 'Saving…' : 'Save reply'}</button>
            {f.reply && (
              <button type="button" disabled={busy} onClick={clearReply} className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">Clear reply</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


const emptyForm = {
  type: 'meditation',
  title: '',
  shortTitle: '',
  language: 'English',
  langFlag: '🇺🇸',
  category: 'Mindfulness',
  format: 'Audio',
  duration: 5,
  image: IMAGES.forest,
  description: '',
  note: '',
  written: '',
  featured: false,
  youtubeId: '',
  audioUrl: '',
};

const imagePresets = [
  { id: 'lake', label: 'Serene Lake (Sunrise)', url: IMAGES.lake },
  { id: 'stars', label: 'Night Sky (Deep Sleep)', url: IMAGES.stars },
  { id: 'forest', label: 'Forest Sunbeams (Mindfulness)', url: IMAGES.forest },
];

const AdminModal = ({ onClose, sessions = [], onSessionsChange }) => {
  const [screen, setScreen] = useState('login'); // login | reset | portal
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showPass, setShowPass] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState('');
  const [newCode, setNewCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');

  const [tab, setTab] = useState('meditations'); // meditations | affirmations | new | feedback
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [feedbackAll, setFeedbackAll] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  // Security tab state
  const [secCurrent, setSecCurrent] = useState('');
  const [secNew, setSecNew] = useState('');
  const [secConfirm, setSecConfirm] = useState('');
  const [secShow, setSecShow] = useState(false);
  const [secBusy, setSecBusy] = useState(false);
  const [secMsg, setSecMsg] = useState('');
  const [secErr, setSecErr] = useState('');

  // Security Key state
  const [skStatus, setSkStatus] = useState({ hasSecurityKey: false, length: 0 });
  const [skNew, setSkNew] = useState('');
  const [skConfirm, setSkConfirm] = useState('');
  const [skShow, setSkShow] = useState(false);
  const [skBusy, setSkBusy] = useState(false);
  const [skMsg, setSkMsg] = useState('');
  const [skErr, setSkErr] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('wwm_admin_passcode')) {
      setPasscode(sessionStorage.getItem('wwm_admin_passcode'));
      setScreen('portal');
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    const clean = (passcode || '').trim();
    try {
      await api.verifyAdmin(clean);
      sessionStorage.setItem('wwm_admin_passcode', clean);
      setScreen('portal');
    } catch (_e) {
      setError('Incorrect passcode. Please try again.');
    } finally { setLoading(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setError('');
    if (!newCode || newCode !== confirmCode) { setError('Passcodes do not match.'); return; }
    if (newCode.length < 6) { setError('New passcode must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await api.resetPasscode(recoveryKey.trim(), newCode.trim());
      setPasscode(newCode.trim());
      setScreen('login');
      setNewCode(''); setConfirmCode(''); setRecoveryKey('');
      setError('Passcode reset successfully. Please log in with your new passcode.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Unable to reset passcode.';
      setError(detail);
    } finally { setLoading(false); }
  };

  const meds = useMemo(() => sessions.filter((s) => s.type === 'meditation'), [sessions]);
  const affs = useMemo(() => sessions.filter((s) => s.type === 'affirmation'), [sessions]);
  const allCats = useMemo(() => {
    const base = form.type === 'meditation' ? MED_CATS : AFF_CATS;
    return base.filter((c) => !c.startsWith('All'));
  }, [form.type]);

  const openNew = (type) => {
    setEditing(null);
    setForm({ ...emptyForm, type });
    setTab('new');
    setMsg('');
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      type: s.type, title: s.title, shortTitle: s.shortTitle || '',
      language: s.language, langFlag: s.langFlag, category: s.category, format: s.format,
      duration: s.duration, image: s.image, description: s.description, note: s.note,
      written: s.written || '', featured: s.featured, youtubeId: s.youtubeId || '',
      audioUrl: s.audioUrl || '',
    });
    setTab('new');
    setMsg('');
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      // Normalize YouTube URL -> 11-char ID before saving
      const normalizedYt = form.youtubeId ? (extractYouTubeId(form.youtubeId) || form.youtubeId) : '';
      const payload = { ...form, youtubeId: normalizedYt };
      let updatedList;
      if (editing) {
        const updated = await api.updateSession(editing.id, payload);
        updatedList = sessions.map((x) => x.id === updated.id ? updated : x);
        setMsg('Saved.');
      } else {
        const created = await api.createSession(payload);
        updatedList = [...sessions, created];
        setMsg('Created.');
      }
      onSessionsChange && onSessionsChange(updatedList);
      setEditing(null);
      setForm({ ...emptyForm, type: form.type });
      setTab(form.type === 'meditation' ? 'meditations' : 'affirmations');
    } catch (_e) {
      setMsg('Save failed. Check fields and try again.');
    } finally { setSaving(false); }
  };

  const toggleFeatured = async (s) => {
    try {
      const updated = await api.updateSession(s.id, { featured: !s.featured });
      onSessionsChange && onSessionsChange(sessions.map((x) => x.id === updated.id ? updated : x));
    } catch (_e) { /* ignore */ }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.shortTitle || s.title}"?`)) return;
    try {
      await api.deleteSession(s.id);
      onSessionsChange && onSessionsChange(sessions.filter((x) => x.id !== s.id));
    } catch (_e) { /* ignore */ }
  };

  const handleImageUpload = (file) => {
    setUploadErr('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadErr('Please choose an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadErr('Image is too large (max 10MB).'); return; }
    setUploadingImg(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxSide = 1200;
        let { width, height } = img;
        if (width > height && width > maxSide) { height = Math.round((maxSide / width) * height); width = maxSide; }
        else if (height > maxSide) { width = Math.round((maxSide / height) * width); height = maxSide; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        // Try JPEG at 0.8 first, downshift quality if data URL is huge
        let dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        let quality = 0.8;
        while (dataUrl.length > 800 * 1024 && quality > 0.35) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        if (dataUrl.length > 1.2 * 1024 * 1024) {
          setUploadErr('Image is still too large after compression. Try a smaller / lower-resolution image.');
        } else {
          setForm((f) => ({ ...f, image: dataUrl }));
        }
        setUploadingImg(false);
      };
      img.onerror = () => { setUploadErr('Could not read image.'); setUploadingImg(false); };
      img.src = ev.target.result;
    };
    reader.onerror = () => { setUploadErr('Could not read file.'); setUploadingImg(false); };
    reader.readAsDataURL(file);
  };

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try { setFeedbackAll(await api.adminFeedback()); }
    catch (_e) { /* ignore */ }
    finally { setFeedbackLoading(false); }
  };

  useEffect(() => { if (screen === 'portal' && tab === 'feedback') loadFeedback(); }, [screen, tab]);

  useEffect(() => {
    if (screen === 'portal' && tab === 'security') {
      (async () => {
        try { setSkStatus(await api.getSecurityKeyStatus()); } catch (_e) { /* ignore */ }
      })();
    }
  }, [screen, tab]);

  const submitSecurityKey = async (e) => {
    e.preventDefault();
    setSkBusy(true); setSkMsg(''); setSkErr('');
    try {
      await api.setSecurityKey(skNew.trim(), skConfirm.trim());
      setSkMsg('Security Key saved. Store it somewhere private \u2014 it\u2019s never shown again.');
      setSkNew(''); setSkConfirm('');
      setSkStatus(await api.getSecurityKeyStatus());
    } catch (err) {
      setSkErr(err?.response?.data?.detail || 'Failed to save Security Key.');
    } finally { setSkBusy(false); }
  };

  const logout = () => {
    sessionStorage.removeItem('wwm_admin_passcode');
    setPasscode(''); setScreen('login'); setEditing(null);
    onClose && onClose();
  };

  const submitPasscodeChange = async (e) => {
    e.preventDefault();
    setSecBusy(true); setSecMsg(''); setSecErr('');
    const cur = secCurrent.trim();
    const nw = secNew.trim();
    const cf = secConfirm.trim();
    if (nw.length < 6) { setSecErr('New passcode must be at least 6 characters.'); setSecBusy(false); return; }
    if (nw !== cf) { setSecErr('New passcode and confirmation do not match.'); setSecBusy(false); return; }
    try {
      await api.changePasscode(cur, nw);
      sessionStorage.setItem('wwm_admin_passcode', nw);
      setSecMsg('Passcode updated. Your session now uses the new passcode.');
      setSecCurrent(''); setSecNew(''); setSecConfirm('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to update passcode.';
      setSecErr(detail);
    } finally { setSecBusy(false); }
  };

  const renderList = (list, type) => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-emerald-900/70 text-sm">{list.length} {type === 'meditation' ? 'meditations' : 'affirmations'} saved.</div>
        <button onClick={() => openNew(type)} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-700 text-white text-sm hover:bg-emerald-800">
          <Plus className="w-4 h-4" /> New {type === 'meditation' ? 'Meditation' : 'Affirmation'}
        </button>
      </div>
      <div className="rounded-xl border border-emerald-100 overflow-hidden bg-white">
        <div className="hidden md:grid grid-cols-[80px_1fr_100px_130px_80px_80px_120px] gap-3 items-center px-4 py-2 bg-emerald-50 text-[11px] uppercase tracking-widest text-emerald-800">
          <div>Cover</div><div>Title</div><div>Lang</div><div>Category</div><div>Fmt</div><div>Dur</div><div className="text-right">Actions</div>
        </div>
        {list.length === 0 && <div className="text-sm text-emerald-800/70 p-6 text-center">No entries yet. Click New to add one.</div>}
        {list.map((s) => (
          <div key={s.id} className="grid grid-cols-[60px_1fr_auto] md:grid-cols-[80px_1fr_100px_130px_80px_80px_120px] gap-3 items-center px-4 py-3 border-t border-emerald-50">
            <img src={s.image} alt="" className="w-14 h-10 md:w-16 md:h-11 rounded object-cover" />
            <div>
              <div className="text-sm font-medium text-emerald-950 line-clamp-1">{s.title}</div>
              <div className="md:hidden text-[11px] text-emerald-700 mt-0.5">{s.langFlag} {s.language} · {s.category} · {s.format} · {s.duration}m</div>
            </div>
            <div className="hidden md:block text-xs text-emerald-800">{s.langFlag} {s.language}</div>
            <div className="hidden md:block text-xs text-emerald-800">{s.category}</div>
            <div className="hidden md:block text-xs text-emerald-800">{s.format}</div>
            <div className="hidden md:block text-xs text-emerald-800">{s.duration} m</div>
            <div className="flex items-center gap-1 justify-end col-span-full md:col-span-1">
              <button onClick={() => toggleFeatured(s)} title={s.featured ? 'Unfeature' : 'Feature'} className={`p-1.5 rounded-full border ${s.featured ? 'bg-amber-100 border-amber-300 text-amber-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'}`}>
                <Star className={`w-4 h-4 ${s.featured ? 'fill-amber-500 text-amber-500' : ''}`} />
              </button>
              <button onClick={() => openEdit(s)} title="Edit" className="p-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => remove(s)} title="Delete" className="p-1.5 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={save} className="space-y-4">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setTab(form.type === 'meditation' ? 'meditations' : 'affirmations')} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"><ChevronLeft className="w-4 h-4" /> Back to list</button>
        <div className="ml-auto text-xs text-emerald-700">{editing ? `Editing: ${editing.shortTitle || editing.title.slice(0, 30)}` : 'New session'}</div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-emerald-800 font-medium">Content Type *
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, category: e.target.value === 'meditation' ? 'Mindfulness' : 'Morning Energy' })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white">
            <option value="meditation">Guided Meditation</option>
            <option value="affirmation">Daily Affirmation</option>
          </select>
        </label>
        <label className="text-xs text-emerald-800 font-medium">Language *
          <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value, langFlag: e.target.value === 'Hindi' ? '🇮🇳' : '🇺🇸' })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white">
            <option>English</option>
            <option>Hindi</option>
          </select>
        </label>
      </div>

      <label className="block text-xs text-emerald-800 font-medium">Session Title *
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
      </label>

      <label className="block text-xs text-emerald-800 font-medium">Short Title
        <input value={form.shortTitle} onChange={(e) => setForm({ ...form, shortTitle: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
      </label>

      <div className="grid sm:grid-cols-3 gap-3">
        <label className="text-xs text-emerald-800 font-medium">Category *
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white">
            {allCats.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="text-xs text-emerald-800 font-medium">Format *
          <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white">
            <option>Audio</option>
            <option>Video</option>
          </select>
        </label>
        <label className="text-xs text-emerald-800 font-medium">Duration (min) *
          <input type="number" min="1" max="120" required value={form.duration} onChange={(e) => setForm({ ...form, duration: +e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
        </label>
      </div>

      <div>
        <div className="text-xs text-emerald-800 font-medium mb-1">Cover Image Preset *</div>
        <div className="grid grid-cols-3 gap-2">
          {imagePresets.map((p) => (
            <button type="button" key={p.id} onClick={() => setForm({ ...form, image: p.url })} className={`rounded-lg overflow-hidden border-2 transition-all ${form.image === p.url ? 'border-emerald-600 shadow-md shadow-emerald-100' : 'border-transparent hover:border-emerald-200'}`}>
              <img src={p.url} alt={p.label} className="w-full h-16 object-cover" />
              <div className="text-[10px] px-1 py-1 text-emerald-800 text-center bg-white">{p.label}</div>
            </button>
          ))}
        </div>

        <div className="mt-3 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-700 text-white text-xs cursor-pointer hover:bg-emerald-800">
              <Upload className="w-3.5 h-3.5" /> {uploadingImg ? 'Processing…' : 'Upload your own image'}
              <input type="file" accept="image/*" className="hidden" disabled={uploadingImg} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleImageUpload(f); e.target.value = ''; }} />
            </label>
            <span className="text-[11px] text-emerald-800/80">JPG / PNG · auto-resized to 1200px, ~800KB max</span>
          </div>
          {uploadErr && <div className="mt-2 text-xs text-rose-600">{uploadErr}</div>}
        </div>

        <label className="block text-xs text-emerald-800 font-medium mt-3">Custom Image URL (optional)
          <input value={form.image?.startsWith('data:') ? '' : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder={form.image?.startsWith('data:') ? 'Uploaded image in use — clear to paste a URL' : 'https://...'} />
        </label>

        {form.image && (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest text-emerald-700 mb-1">Cover Preview</div>
            <div className="relative w-full max-w-xs h-32 rounded-lg overflow-hidden border border-emerald-200">
              <img src={form.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-emerald-800 font-medium">Audio File URL (MP3, streams inline)
          <input value={form.audioUrl} onChange={(e) => setForm({ ...form, audioUrl: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="https://.../track.mp3 — leave blank for ambient synth" />
        </label>
        <label className="text-xs text-emerald-800 font-medium">YouTube Link or Video ID (auto-detected)
          <input value={form.youtubeId} onChange={(e) => setForm({ ...form, youtubeId: e.target.value })} onBlur={(e) => { const id = extractYouTubeId(e.target.value); if (id) setForm((f) => ({ ...f, youtubeId: id })); }} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="Paste full YouTube URL or 11-char ID" />
          {form.youtubeId && (
            <div className="mt-1 text-[11px] text-emerald-700">
              {extractYouTubeId(form.youtubeId) ? <span>&#10003; Parsed video ID: <code className="text-emerald-900">{extractYouTubeId(form.youtubeId)}</code></span> : <span className="text-rose-600">Could not detect a valid YouTube ID from this value.</span>}
            </div>
          )}
        </label>
      </div>

      <label className="block text-xs text-emerald-800 font-medium">Written Affirmation Text (optional / for affirmations)
        <textarea rows={2} value={form.written} onChange={(e) => setForm({ ...form, written: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
      </label>

      <label className="block text-xs text-emerald-800 font-medium">Short Description *
        <textarea required rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
      </label>

      <label className="block text-xs text-emerald-800 font-medium">Creator&apos;s Personal Note *
        <textarea required rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
      </label>

      <label className="flex items-center gap-2 text-sm text-emerald-800">
        <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-emerald-700" />
        Feature on Spotlight (Hero)
      </label>

      {msg && <div className="text-sm text-emerald-800">{msg}</div>}

      <div className="flex items-center gap-2 pt-2">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : editing ? 'Save & Update Live' : 'Publish Live to Cloud DB'}
        </button>
        <button type="button" onClick={() => { setEditing(null); setForm(emptyForm); setTab(form.type === 'meditation' ? 'meditations' : 'affirmations'); }} className="px-4 py-2.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">Cancel</button>
      </div>
    </form>
  );

  const renderFeedback = () => {
    const updateFeedbackLocal = (id, patch) => {
      setFeedbackAll((list) => list.map((f) => f.id === id ? { ...f, ...patch } : f));
    };
    const toggleVisibility = async (f) => {
      try { const upd = await api.updateFeedback(f.id, { visible: !f.visible }); updateFeedbackLocal(f.id, upd); } catch (_e) { /* ignore */ }
    };
    const deleteEntry = async (f) => {
      if (!window.confirm(`Delete feedback from ${f.name}? This cannot be undone.`)) return;
      try { await api.deleteFeedback(f.id); setFeedbackAll((list) => list.filter((x) => x.id !== f.id)); } catch (_e) { /* ignore */ }
    };
    const saveReply = async (f, replyText) => {
      try { const upd = await api.updateFeedback(f.id, { reply: replyText }); updateFeedbackLocal(f.id, upd); } catch (_e) { /* ignore */ }
    };
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="text-emerald-900/70 text-sm">{feedbackAll.length} entries</div>
          <button onClick={loadFeedback} className="text-xs text-emerald-700 hover:underline">Refresh</button>
        </div>
        {feedbackLoading && <div className="text-sm text-emerald-700">Loading\u2026</div>}
        <div className="space-y-3">
          {feedbackAll.map((f) => (
            <FeedbackAdminRow key={f.id} f={f} onToggleVisibility={() => toggleVisibility(f)} onDelete={() => deleteEntry(f)} onSaveReply={(txt) => saveReply(f, txt)} />
          ))}
        </div>
      </div>
    );
  };

  const renderSecurity = () => (
    <div className="max-w-lg space-y-6">
      <div className="rounded-2xl border border-emerald-100 bg-white p-5">
        <div className="flex items-center gap-2 text-emerald-800">
          <ShieldAlert className="w-4 h-4 text-emerald-700" />
          <div className="text-[10px] uppercase tracking-widest">Security</div>
        </div>
        <h3 className="mt-1 font-serif text-2xl text-emerald-950">Change Admin Passcode</h3>
        <p className="mt-1 text-sm text-emerald-900/70">Update your creator passcode. New passcode is stored securely in your Cloud DB.</p>

        <form onSubmit={submitPasscodeChange} className="mt-5 space-y-3">
          <label className="block text-xs text-emerald-800 font-medium">Current Passcode *
            <div className="mt-1 relative">
              <input type={secShow ? 'text' : 'password'} required value={secCurrent} onChange={(e) => setSecCurrent(e.target.value)} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} className="w-full px-3 py-2 pr-16 rounded-lg border border-emerald-200" />
              <button type="button" onClick={() => setSecShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-emerald-700 hover:underline px-2 py-1">{secShow ? 'Hide' : 'Show'}</button>
            </div>
          </label>
          <label className="block text-xs text-emerald-800 font-medium">New Passcode * <span className="text-emerald-600 font-normal">(min 6 characters)</span>
            <input type={secShow ? 'text' : 'password'} required minLength={6} value={secNew} onChange={(e) => setSecNew(e.target.value)} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
          </label>
          <label className="block text-xs text-emerald-800 font-medium">Confirm New Passcode *
            <input type={secShow ? 'text' : 'password'} required minLength={6} value={secConfirm} onChange={(e) => setSecConfirm(e.target.value)} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
          </label>

          {secErr && <div className="text-sm text-rose-600">{secErr}</div>}
          {secMsg && <div className="text-sm text-emerald-700 inline-flex items-center gap-1"><Check className="w-4 h-4" /> {secMsg}</div>}

          <button type="submit" disabled={secBusy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
            <KeyRound className="w-4 h-4" /> {secBusy ? 'Updating\u2026' : 'Update Passcode'}
          </button>
        </form>
      </div>

      <div className="rounded-2xl border border-emerald-100 bg-white p-5">
        <div className="flex items-center gap-2 text-emerald-800">
          <KeyRound className="w-4 h-4 text-emerald-700" />
          <div className="text-[10px] uppercase tracking-widest">Recovery</div>
        </div>
        <h3 className="mt-1 font-serif text-2xl text-emerald-950">Private Security Key</h3>
        <p className="mt-1 text-sm text-emerald-900/70">
          {skStatus.hasSecurityKey
            ? 'A Security Key is already set. You can replace it below \u2014 the old key stops working immediately.'
            : 'No Security Key set yet. Set one now so you can recover access if you ever forget your passcode.'}
        </p>

        <form onSubmit={submitSecurityKey} className="mt-5 space-y-3">
          <label className="block text-xs text-emerald-800 font-medium">{skStatus.hasSecurityKey ? 'New Security Key *' : 'Security Key *'} <span className="text-emerald-600 font-normal">(min 6 characters)</span>
            <div className="mt-1 relative">
              <input type={skShow ? 'text' : 'password'} required minLength={6} value={skNew} onChange={(e) => setSkNew(e.target.value)} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} className="w-full px-3 py-2 pr-16 rounded-lg border border-emerald-200" />
              <button type="button" onClick={() => setSkShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-emerald-700 hover:underline px-2 py-1">{skShow ? 'Hide' : 'Show'}</button>
            </div>
          </label>
          <label className="block text-xs text-emerald-800 font-medium">Confirm Security Key *
            <input type={skShow ? 'text' : 'password'} required minLength={6} value={skConfirm} onChange={(e) => setSkConfirm(e.target.value)} autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck={false} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
          </label>

          {skErr && <div className="text-sm text-rose-600">{skErr}</div>}
          {skMsg && <div className="text-sm text-emerald-700 inline-flex items-center gap-1"><Check className="w-4 h-4" /> {skMsg}</div>}

          <button type="submit" disabled={skBusy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
            <KeyRound className="w-4 h-4" /> {skBusy ? 'Saving\u2026' : skStatus.hasSecurityKey ? 'Replace Security Key' : 'Save Security Key'}
          </button>
        </form>

        <div className="mt-5 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-800/90">
          <div className="font-medium mb-1">How the Security Key works</div>
          <ul className="list-disc pl-4 space-y-0.5 text-emerald-900/80">
            <li>If you ever forget your passcode, click <strong>Forgot Passcode?</strong> on the login and enter this Security Key to set a new passcode.</li>
            <li>Write it down and store it somewhere safe (password manager or paper wallet). It is never shown again after saving.</li>
            <li>Use something different from your passcode.</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderPortal = () => (
    <div className="w-full max-w-5xl max-h-[95vh] overflow-y-auto rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-white z-10 border-b border-emerald-100 px-6 py-4 flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700">Content Manager</div>
            <div className="font-serif text-xl text-emerald-950">Admin Portal — winwithmeditation.com</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Cloud DB Connected
          </span>
          <button onClick={logout} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-900 text-white text-xs hover:bg-emerald-800">
            <Lock className="w-3.5 h-3.5" /> Lock &amp; Log Out
          </button>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-emerald-50"><X className="w-5 h-5 text-emerald-900" /></button>
        </div>
      </div>

      <div className="px-6 pt-4 flex flex-wrap items-center gap-1 border-b border-emerald-100">
        {[
          { id: 'meditations', label: `Manage Meditations (${meds.length})`, icon: LibraryBig },
          { id: 'affirmations', label: `Manage Affirmations (${affs.length})`, icon: LibraryBig },
          { id: 'new', label: editing ? 'Edit Session' : 'Upload New Content', icon: Plus },
          { id: 'blogs', label: 'Manage Blogs & Guides', icon: BookOpen },
          { id: 'feedback', label: 'Feedback Inbox', icon: Inbox },
          { id: 'security', label: 'Security & Passcode', icon: ShieldAlert },
        ].map((t) => (
          <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'new' && !editing) { setForm(emptyForm); } }} className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 -mb-px ${tab === t.id ? 'border-emerald-700 text-emerald-800 font-medium' : 'border-transparent text-emerald-700/70 hover:text-emerald-800'}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-6">
        {tab === 'meditations' && renderList(meds, 'meditation')}
        {tab === 'affirmations' && renderList(affs, 'affirmation')}
        {tab === 'new' && renderForm()}
        {tab === 'blogs' && <BlogManager />}
        {tab === 'feedback' && renderFeedback()}
        {tab === 'security' && renderSecurity()}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      {screen === 'login' && (
        <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-emerald-800"><Lock className="w-5 h-5" /><span className="text-xs uppercase tracking-widest">Content Manager</span></div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-emerald-50"><X className="w-5 h-5 text-emerald-900" /></button>
          </div>
          <h3 className="mt-3 font-serif text-2xl text-emerald-950">Creator Admin Login</h3>
          <p className="mt-1 text-sm text-emerald-900/70">Enter your secret passcode to access Content Manager for <strong>winwithmeditation.com</strong>.</p>
          <form onSubmit={login} className="mt-5 space-y-3">
            <label className="block text-xs text-emerald-800 font-medium">Admin Passcode *
              <div className="mt-1 relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full px-3 py-2 pr-16 rounded-lg border border-emerald-200 focus:ring-2 focus:ring-emerald-300 focus:outline-none"
                  placeholder="Enter your passcode"
                />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-emerald-700 hover:underline px-2 py-1">
                  {showPass ? 'Hide' : 'Show'}
                </button>
              </div>
            </label>
            {error && <div className="text-sm text-rose-600">{error}</div>}
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
              <Lock className="w-4 h-4" /> {loading ? 'Verifying…' : 'Unlock Admin Portal'}
            </button>
            <button type="button" onClick={() => { setScreen('reset'); setError(''); }} className="w-full text-center text-xs text-emerald-700 hover:underline">Forgot Passcode?</button>
          </form>
        </div>
      )}
      {screen === 'reset' && (
        <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-emerald-800"><KeyRound className="w-5 h-5" /><span className="text-xs uppercase tracking-widest">Passcode Reset</span></div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-emerald-50"><X className="w-5 h-5 text-emerald-900" /></button>
          </div>
          <h3 className="mt-3 font-serif text-2xl text-emerald-950">Secure Passcode Reset</h3>
          <p className="mt-1 text-sm text-emerald-900/70">Enter your private Security Key to verify ownership.</p>
          <form onSubmit={reset} className="mt-5 space-y-3">
            <input required value={recoveryKey} onChange={(e) => setRecoveryKey(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="Private Security Key" />
            <input required type="password" value={newCode} onChange={(e) => setNewCode(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="New Passcode" />
            <input required type="password" value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="Confirm New Passcode" />
            {error && <div className="text-sm text-rose-600">{error}</div>}
            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800">
              <KeyRound className="w-4 h-4" /> Verify &amp; Reset Passcode
            </button>
            <button type="button" onClick={() => { setScreen('login'); setError(''); }} className="w-full text-center text-xs text-emerald-700 hover:underline">Back to login</button>
          </form>
        </div>
      )}
      {screen === 'portal' && renderPortal()}
    </div>
  );
};

export default AdminModal;
